import { Box, Button, Drawer, Grid } from "@mui/material";
import { useSnackbar } from "notistack";
import React from "react";
import { confirmAlert } from "react-confirm-alert";
import { useParams } from "react-router-dom";
import { ApiError } from "../../Api/ApiError";
import { getStudentList, getTeacherData, LessonStudents, RegisterStudent, UnregisterStudent, UnregisterTeacher} from "../../Api/OnlineLesson";
import { CustomerInterface } from "../../Objects/Customer";
import { LessonStudent, LessonTeacher } from "../../Objects/OnlineLesson";
import PageTitle from "../atoms/PageTitle";
import CustomerCard from "../molecules/CustomerCard";
import LessonTeacherCard from "../molecules/LessonTeacherCard";
import LessonReservableList from "../organisms/LessonReservableList";
import LessonStudentList from "../organisms/LessonStudentList";
import LessonUnreservableList from "../organisms/LessonUnreservableList";
import SelectCustomerArea from "../organisms/SelectCustomerArea";

const LessonTeacherPage: React.FC = () => {
    const DISPLAY_ROWS_STUDENT_LIST = 6
    const DISPLAY_ROWS_LESSON_RESERVABLE_LIST = 6
    const DISPLAY_ROWS_LESSON_UNRESERVABLE_LIST = 6
    let { teacher_id } = useParams()
    let [teacherData, setTeacherData] = React.useState<LessonTeacher>()
    let [lessonStudents, setLessonStudents] = React.useState<LessonStudents>()
    let [regStudentOpen, setRegStudentOpen] = React.useState(false)
    let [pagenumStudentList, setPagenumStudentList] = React.useState(1)
    let { enqueueSnackbar } = useSnackbar()

    const abortController = React.useMemo(() => new AbortController(), [])

    React.useEffect(() => {
        return () => {
            abortController.abort()
        }
    }, [abortController])

    const loadStudentList = React.useCallback(() => {
        if( teacherData ) {
            getStudentList({
                limit: DISPLAY_ROWS_STUDENT_LIST,
                offset: (pagenumStudentList - 1) * DISPLAY_ROWS_STUDENT_LIST,
                teacher_id: teacherData.teacher_id
            }, {signal: abortController.signal}).then(lessonStudents => {
                setLessonStudents(lessonStudents)
            }).catch((e: any) => {
                if( e.__CANCEL__ ) {
                    console.info("canceled")
                } else if (e instanceof ApiError) {
                    enqueueSnackbar(e.message, { variant: "error" })
                } else if (e instanceof Error) {
                    enqueueSnackbar(e.message, { variant: "error" })
                }else {
                    enqueueSnackbar("通信に失敗しました", { variant: "error" })
                }
            })
        }
    }, [abortController, teacherData, enqueueSnackbar, pagenumStudentList])

    let loadTeacherData = React.useCallback(() => {
        if (teacher_id) {
            getTeacherData(parseInt(teacher_id), {signal: abortController.signal}).then(teacherData => {
                setTeacherData(teacherData)
            }).catch((e: any) => {
                if( e.__CANCEL__ ) {
                    console.info("canceled")
                } else if (e instanceof ApiError) {
                    enqueueSnackbar(e.message, { variant: "error" })
                } else if (e instanceof Error) {
                    enqueueSnackbar(e.message, { variant: "error" })
                }else {
                    enqueueSnackbar("通信に失敗しました", { variant: "error" })
                }
            })
        }
    }, [abortController, teacher_id, enqueueSnackbar])

    const doUnregisterTeacher = React.useCallback(() => {
        if (teacherData) {
            UnregisterTeacher(teacherData.teacher_id, {signal: abortController.signal}).then(() => {
                enqueueSnackbar("講師登録を解除しました", { variant: "success" })
                loadTeacherData()
            }).catch((e: any) => {
                if( e.__CANCEL__ ) {
                    console.info("canceled")
                } else if (e instanceof ApiError) {
                    enqueueSnackbar(e.message, { variant: "error" })
                } else if (e instanceof Error) {
                    enqueueSnackbar(e.message, { variant: "error" })
                }else {
                    enqueueSnackbar("通信に失敗しました", { variant: "error" })
                }
            })
        }
    }, [abortController, teacherData, enqueueSnackbar, loadTeacherData])

    const doRegisterStudent = React.useCallback((customerData: CustomerInterface) => {
        if (teacher_id) {
            RegisterStudent({ ctm_id: customerData.ctm_id, teacher_id: parseInt(teacher_id) }, {signal: abortController.signal}).then(student_id => {
                enqueueSnackbar("登録しました", { variant: "success" })
                setRegStudentOpen(false)
                loadTeacherData()
            }).catch((e: any) => {
                if( e.__CANCEL__ ) {
                    console.info("canceled")
                } else if (e instanceof ApiError) {
                    enqueueSnackbar(e.message, { variant: "error" })
                } else if (e instanceof Error) {
                    enqueueSnackbar(e.message, { variant: "error" })
                }else {
                    enqueueSnackbar("通信に失敗しました", { variant: "error" })
                }
            })
        }
    }, [abortController, enqueueSnackbar, loadTeacherData, teacher_id])

    const doUnregisterStudent = React.useCallback((lessonStudent: LessonStudent) => {
        if (teacherData) {
            UnregisterStudent({ teacher_id: teacherData.teacher_id, student_id: lessonStudent.student_id }, {signal: abortController.signal}).then(() => {
                enqueueSnackbar("登録を解除しました", { variant: "success" })
                loadTeacherData()
            }).catch((e: any) => {
                if( e.__CANCEL__ ) {
                    console.info("canceled")
                } else if (e instanceof ApiError) {
                    enqueueSnackbar(e.message, { variant: "error" })
                } else if (e instanceof Error) {
                    enqueueSnackbar(e.message, { variant: "error" })
                }else {
                    enqueueSnackbar("通信に失敗しました", { variant: "error" })
                }
            })
        }
    }, [abortController, teacherData, loadTeacherData, enqueueSnackbar])

    React.useEffect(() => {
        loadStudentList()
    }, [loadStudentList])

    React.useEffect(() => {
        loadTeacherData()
    }, [loadTeacherData])

    return (
        <>
            <PageTitle text="オンラインレッスン" />
            {teacherData &&
                <Grid container spacing={2}>
                    <Grid item xs={12} sm={6} md={4}>
                        {teacherData.customerData &&
                            <CustomerCard customer={teacherData.customerData} />
                        }
                    </Grid>
                    <Grid item xs={12} sm={6} md={8} sx={{
                        display: "flex",
                        flexDirection: "column",
                        justifyContent: "space-between",
                    }}>
                        <LessonTeacherCard lessonTeacher={teacherData} />
                        <Box sx={{
                            display: "flex",
                            alignItems: "flex-end"
                        }}>
                            {!teacherData.delete_at &&
                                <Button
                                    variant="contained"
                                    onClick={() => {
                                        confirmAlert({
                                            message: "講師登録を解除します。",
                                            buttons: [
                                                {
                                                    label: "キャンセル",
                                                    onClick: () => { }
                                                },
                                                {
                                                    label: "実行",
                                                    onClick: () => {
                                                        doUnregisterTeacher()
                                                    }
                                                }
                                            ]
                                        })
                                    }}
                                >講師の登録を解除</Button>
                            }
                        </Box>
                    </Grid>
                    <Grid item xs={12}>
                        <LessonReservableList
                            // lessonReservables={lessonReservables}
                            teacher_id={teacher_id ? parseInt(teacher_id) : 0}
                            display_rows={DISPLAY_ROWS_LESSON_RESERVABLE_LIST}
                            // requestAdd={(week: WeekType, startTime: Time, endTime: Time, startApply?: Date, endApply?: Date) => {
                            //     (async () => {
                            //         try {
                            //             if (teacher_id) {
                            //                 let lrvable_id = await addLessonReservable({
                            //                     teacher_id: parseInt(teacher_id),
                            //                     week_conditions: week,
                            //                     start: startTime,
                            //                     end: endTime,
                            //                     start_apply: startApply,
                            //                     end_apply: endApply
                            //                 })
                            //             }
                            //         } catch (error) {
                            //             return false
                            //         }
                            //     })()
                            //     loadTeacherData()
                            //     return true
                            // }}
                            // requestEdit={lessonReservables => {
                            //     (async () => {
                            //         try {
                            //             if (teacher_id) {
                            //                 await editLessonReservable({
                            //                     lrvable_id: lessonReservables.lrvable_id,
                            //                     teacher_id: lessonReservables.teacher_id,
                            //                     week_conditions: lessonReservables.week_conditions,
                            //                     start: lessonReservables.start,
                            //                     end: lessonReservables.end,
                            //                     start_apply: lessonReservables.start_apply,
                            //                     end_apply: lessonReservables.end_apply
                            //                 })
                            //             }
                            //         } catch (error) {
                            //             return false
                            //         }
                            //     })()
                            //     loadTeacherData()
                            //     console.log(lessonReservables)
                            //     return true
                            // }}
                            // onPageChange={page => {
                            //     setPagenumLessonReservable(page)
                            // }}
                        />
                    </Grid>
                    <Grid item xs={12}>
                        <LessonUnreservableList
                            // lessonUnreservables={lessonUnreservables}
                            teacher_id={teacher_id ? parseInt(teacher_id) : 0}
                            display_rows={DISPLAY_ROWS_LESSON_UNRESERVABLE_LIST}
                            // requestAdd={(lurvable_date?: Date, startTime?: Time, endTime?: Time) => {
                            //     let result : boolean = false;
                            //     (async () => {
                            //         try {
                            //             if (teacher_id) {
                            //                 let lurvable_id = await addLessonUnreservable({
                            //                     teacher_id: parseInt(teacher_id),
                            //                     lurvable_date: lurvable_date,
                            //                     start: startTime,
                            //                     end: endTime,
                            //                 })
                            //             }
                            //         } catch (error) {
                            //             result = false
                            //             return
                            //         }
                            //         result = true
                            //     })()
                            //     loadTeacherData()
                            //     return result
                            // }}
                            // requestEdit={lessonUnreservables => {
                            //     let result : boolean = false;
                            //     (async () => {
                            //         try {
                            //             if (teacher_id) {
                            //                 await editLessonUnreservable({
                            //                     lurvable_id: lessonUnreservables.lurvable_id,
                            //                     teacher_id: lessonUnreservables.teacher_id,
                            //                     lurvable_date: lessonUnreservables.lurvable_date,
                            //                     start: lessonUnreservables.start,
                            //                     end: lessonUnreservables.end,
                            //                 })
                            //             }
                            //         } catch (error) {
                            //             result = false
                            //             console.log("false...")
                            //             return
                            //         }
                            //         console.log("true!!!")
                            //         result = true
                            //     })();
                            //     console.log(result)
                            //     loadTeacherData()
                            //     return result
                            // }}
                            // onPageChange={page => {
                            //     setPagenumLessonUnreservable(page)
                            // }}
                        />
                    </Grid>
                    <Grid item xs={12}>
                        {lessonStudents &&
                            <LessonStudentList
                                lessonStudents={lessonStudents}
                                requestRegStudent={() => {
                                    setRegStudentOpen(true)
                                }}
                                requestUnregStudent={lessonStudent => {
                                    let message = ""
                                    if (lessonStudent.customerData) {
                                        message = lessonStudent.customerData.ctm_name1 + " " + lessonStudent.customerData.ctm_name2 + "の"
                                    }
                                    message += "登録を解除しますか"
                                    confirmAlert({
                                        message: message,
                                        buttons: [
                                            {
                                                label: "キャンセル",
                                                onClick: () => { }
                                            },
                                            {
                                                label: "実行",
                                                onClick: () => {
                                                    doUnregisterStudent(lessonStudent)
                                                }
                                            },
                                        ]

                                    })
                                }}
                                onClick={() => { }}
                                onPageChange={page => {
                                    setPagenumStudentList(page)
                                }}
                            />
                        }
                    </Grid>
                </Grid>
            }
            <Drawer
                anchor="bottom"
                open={regStudentOpen}
                onClose={() => {
                    setRegStudentOpen(false)
                }}
                sx={{
                    zIndex: 1210
                }}
                PaperProps={{
                    sx: {
                        height: "50%",
                        borderRadius: "1em 1em 0 0"
                    }
                }}
            >
                <Box sx={{ padding: "14px" }}>
                    <Box sx={{ display: "flex", justifyContent: "right" }}>
                        <Button
                            onClick={() => {
                                setRegStudentOpen(false)
                            }}
                        >閉じる</Button>
                    </Box>
                    <PageTitle text="オンラインレッスン生徒登録" />
                    <SelectCustomerArea
                        onSelect={customerData => {
                            doRegisterStudent(customerData)
                            // RegisterTeacher(customerData.ctm_id).then(teacher_id => {
                            //     enqueueSnackbar("登録しました", { variant: "success" })
                            //     setRegTeacherOpen(false)
                            //     loadTeacherList()
                            // }).catch(e => {
                            //     enqueueSnackbar(e.message, { variant: "error" })
                            // })
                        }}
                    />
                </Box>
            </Drawer>
        </>
    )
}

export default LessonTeacherPage